export interface CancellationPolicyConfig {
    startDate: Date;
    cancellationPolicy: string;
}

export const formatDate = (date: Date) => {
    const month = ["Jan", "Feb", "Mar", "Apr", "May", "Jun", "Jul", "Aug", "Sep", "Oct", "Nov", "Dec"];
    return `${date.getUTCDate()} ${month[date.getUTCMonth()]}`;
};

export const calculatePolicyDate = (startDate: Date, daysBefore: number) => {
    const date = new Date(startDate);
    date.setUTCDate(date.getUTCDate() - daysBefore);
    return date;
};

export const daysToArrivalDate = (arrivalDate: string) => {
    const today = new Date(new Date().toString().split(" ").slice(1, 5).join(" ") + " GMT+0000");
    const millisecond = new Date(arrivalDate).getTime() - today.getTime();
    const day = Math.ceil(millisecond / 1000 / 3600 / 24);
    return day;
};

export const getTime = (time: number) => {
    if (time < 100 || time > 2400) {
        return null;
    }
    const ampm = time > 1100 && time < 2400 ? " pm" : " am";
    const hour = time === 1200 || time === 2400 ? 12 : (time / 100) % 12;
    return `${hour}:00${ampm}`;
};

// Guest-side cancellation policy functions
export const getCancellationPolicyText = ({ startDate, cancellationPolicy }: CancellationPolicyConfig) => {
    if (!startDate) return "";
    
    const currentDate = new Date();
    
    switch (cancellationPolicy) {
        case "flexible":
            const flexibleDate = calculatePolicyDate(startDate, 1);
            return {
                date: formatDate(flexibleDate),
                fallbackText: "until 24 hours before arrival",
                description: "After that, you will receive a full refund of unspent nights minus service fee."
            };
        case "moderate":
            const moderateDate = calculatePolicyDate(startDate, 7);
            return {
                date: formatDate(moderateDate),
                fallbackText: "until 7 days before arrival",
                description: "After that, you will receive a 50% refund of unspent nights minus service fee."
            };
        case "strict":
            const strictDate = new Date(currentDate);
            strictDate.setUTCDate(currentDate.getUTCDate() + 2);
            const strictRefundDate = calculatePolicyDate(startDate, 28);
            return {
                date: formatDate(strictDate),
                refundDate: formatDate(strictRefundDate),
                fallbackText: "before 2 days from now",
                refundFallbackText: "28 days before arrival",
                description: "If you cancel before the refund date you will receive a 50% refund minus service fees. After that, you won't be refunded."
            };
        default:
            return null;
    }
};

export const getSimpleCancellationPolicyText = (cancellationPolicy: string) => {
    switch (cancellationPolicy) {
        case "flexible":
            return "You will get a full refund of unspent nights minus service fee and cleaning fee if applied.";
        case "moderate":
            return "You will get a 50% refund of unspent nights minus service fee and cleaning fee if applied.";
        case "strict":
            return "If you cancel, you won't be refunded except in an exceptional circumstance.";
        default:
            return "";
    }
};

// Host-side cancellation policy functions
export const getHostCancellationPolicyText = ({ startDate, cancellationPolicy }: CancellationPolicyConfig) => {
    if (!startDate) return "";
    
    switch (cancellationPolicy) {
        case "flexible":
            const flexibleDate = calculatePolicyDate(startDate, 1);
            return {
                date: formatDate(flexibleDate),
                fallbackText: "until 24 hours before arrival",
                description: "After that, if your guest cancels they will receive a full refund of unspent nights minus service fee and cleaning fee if applicable."
            };
        case "moderate":
            const moderateDate = calculatePolicyDate(startDate, 14); // Host uses 14 days, not 7
            return {
                date: formatDate(moderateDate),
                fallbackText: "until 14 days before arrival",
                description: "After that, if your guest cancels they will receive a full refund of half the unspent nights minus service fee and cleaning fee if applicable."
            };
        case "strict":
            const strictDate = calculatePolicyDate(startDate, 28);
            return {
                date: formatDate(strictDate),
                fallbackText: "until 28 days before arrival",
                description: "After that, if your guest cancel less than 2 weeks to arrival, they will be charged for the first week."
            };
        default:
            return null;
    }
};

// Host-side booking request cancellation policy (different business logic)
export const getHostBookingRequestCancellationPolicyText = ({ startDate, cancellationPolicy }: CancellationPolicyConfig) => {
    if (!startDate) return "";
    
    const currentDate = new Date();
    
    switch (cancellationPolicy) {
        case "flexible":
            const flexibleDate = calculatePolicyDate(startDate, 1);
            return {
                date: formatDate(flexibleDate),
                fallbackText: "until 24 hours before arrival",
                description: "After that, you will be paid for every night the guest spends."
            };
        case "moderate":
            const moderateDate = calculatePolicyDate(startDate, 1); // This component uses 1 day, not 14
            return {
                date: formatDate(moderateDate),
                fallbackText: "until 24 hours before arrival", 
                description: "After that, you will be paid for every night spent, plus 50% of unspent nights."
            };
        case "strict":
            const strictDate = new Date(currentDate);
            strictDate.setUTCDate(currentDate.getUTCDate() + 2);
            const strictRefundDate = calculatePolicyDate(startDate, 28);
            return {
                date: formatDate(strictDate),
                refundDate: formatDate(strictRefundDate),
                fallbackText: "before 2 days from now",
                refundFallbackText: "28 days before arrival",
                description: "If you cancel before the refund date you will receive a 50% refund minus service fees. After that, you won't be refunded."
            };
        default:
            return null;
    }
}; 